import { apiClient, orgUiPath } from '@/core/api';
import { auth } from '@/core/firebase';
import { organizationNameFromEmail } from '@/core/organizationName';
import { isUnregisteredError } from '@/core/authErrors';
import { mintAllOrgSessions } from '@/core/sessionAuth';
import { resolveAuthenticatedDestination } from '@/core/urlRouting';
import type { MeResponse, UserProfile } from '@/types/api';
import { isPlatformRoleUser } from '@/utils/platformRole';
export { isUnregisteredError } from '@/core/authErrors';
export { organizationNameFromEmail } from '@/core/organizationName';

const REGISTRATION_RETRY_ATTEMPTS = 12;
const REGISTRATION_RETRY_DELAY_MS = 150;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/** Poll until Datastore email lookup sees the user (emulator index lag after ``/setup``). */
async function waitForUsersMe(): Promise<MeResponse> {
  let lastError: unknown;
  for (let attempt = 0; attempt < REGISTRATION_RETRY_ATTEMPTS; attempt += 1) {
    try {
      return await apiClient<MeResponse>('/users/me');
    } catch (err: unknown) {
      lastError = err;
      const message = err instanceof Error ? err.message : '';
      if (!isUnregisteredError(message)) {
        throw err;
      }
      if (attempt < REGISTRATION_RETRY_ATTEMPTS - 1) {
        await sleep(REGISTRATION_RETRY_DELAY_MS);
      }
    }
  }
  throw lastError instanceof Error ? lastError : new Error('User not registered');
}

/** ``POST /setup`` for the signed-in Firebase user (idempotent). */
export async function autoBootstrapWorkspace(): Promise<number> {
  const email = auth.currentUser?.email;
  if (!email) {
    throw new Error('Signed-in user has no email');
  }
  const setupUser = await apiClient<UserProfile>('/setup', {
    method: 'POST',
    body: JSON.stringify({ organization_name: organizationNameFromEmail(email) }),
  });

  const me = await waitForUsersMe();
  const orgId =
    pickSingleAccessibleOrg(me) ?? setupUser.organization_id ?? me.user.organization_id;
  if (orgId == null) {
    throw new Error('Setup did not return organization_id');
  }

  await mintAllOrgSessions(me.organizations.map((o) => o.id));
  return orgId;
}

export function pickSingleAccessibleOrg(me: MeResponse): number | null {
  if (isPlatformRoleUser(me.user)) return null;
  if (me.organizations.length !== 1) return null;
  const org = me.organizations[0];
  return org.id ?? null;
}

/** Resolve post-login destination; honors safe ``?next=`` when provided. */
export async function resolvePostAuthDestination(pendingReturn?: string | null): Promise<string> {
  try {
    const me = await apiClient<MeResponse>('/users/me');
    await mintAllOrgSessions(me.organizations.map((o) => o.id));
    return resolveAuthenticatedDestination(me, pendingReturn);
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : '';
    if (!isUnregisteredError(message)) {
      throw err;
    }
  }

  const orgId = await autoBootstrapWorkspace();
  const bootPath = orgUiPath(orgId, '/');
  if (pendingReturn) {
    try {
      const me = await apiClient<MeResponse>('/users/me');
      return resolveAuthenticatedDestination(me, pendingReturn);
    } catch {
      return bootPath;
    }
  }
  return bootPath;
}
